<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Quality System Implementation Capacity — QikSolve',
  description:
    'Assess quality-system implementation as a team-capacity decision, not only a software selection, before committing to a delivery pathway.',
});

const page = {
  hero: {
    eyebrow: 'Quality system implementation',
    title: 'Implementing a Practical Quality System Without Overloading the Team',
    summary:
      'A quality system has to fit the team that will design it, validate it, operate it, and maintain it. Implementation capacity is part of the quality decision.',
    cta: { label: 'Discuss your implementation sequence', to: '/contact' },
  },
  intro: {
    title: 'Implementation capacity is part of the quality decision',
    content:
      'Implementation usually includes more than technical setup. Teams need to clarify the process, confirm ownership, define records and approvals, prepare evidence, review intended use, and establish how changes will be managed. Those decisions compete with deviations, CAPA, change control, production support, and routine quality review.',
    items: [
      {
        title: 'Make capacity visible',
        description:
          'The implementation pathway should make capacity visible rather than assume that specialist resource is available.',
      },
      {
        title: 'Assess the work itself',
        description:
          'A system may be capable in theory but impractical for the team expected to own and sustain it.',
      },
    ],
  },
  steps: {
    title: 'Start with a defined quality problem',
    items: [
      {
        title: 'Choose one meaningful process',
        description:
          'Starting with one defined workflow, such as CAPA or change control, keeps the implementation conversation connected to real ownership and daily practice.',
      },
      {
        title: 'Clarify the control model',
        description:
          'Use the workflow to consider records, approvals, evidence, documentation, and controlled change before extending the operating model further.',
      },
      {
        title: 'Plan for sustained ownership',
        description:
          'The important question is whether the design can be understood, reviewed, validated where required, and sustained by the people accountable for the quality process.',
      },
    ],
  },
  sections: [
    {
      title: 'Questions to ask before choosing a pathway',
      items: [
        {
          title: 'Process and ownership',
          description: 'Which quality process needs clearer control first, and who owns its decisions, records, approvals, and evidence?',
        },
        {
          title: 'Existing practice',
          description: 'What existing practice should the system support rather than replace?',
        },
        {
          title: 'Team capacity',
          description: 'What can the team review and maintain after the initial work is complete?',
        },
        {
          title: 'Implementation pathway',
          description: 'What sequence can the organisation understand, validate where required, and sustain?',
        },
      ],
    },
    {
      title: 'What QikSolve supports',
      content:
        "QikSolve supports practical, controlled quality-system design and operation. Its configuration-led approach assesses where existing Microsoft 365 capabilities may support the intended workflow, subject to the organisation's intended use and review.",
    },
  ],
  callout: {
    title: 'Responsibility remains with your organisation',
    content:
      'Your organisation remains responsible for its regulatory decisions, controls, approvals, and oversight; QikSolve is a practitioner-led corporate adviser, not a regulator or certifying body.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue exploring the pathway',
    items: [
      {
        label: 'Planning validation around sustainable teams',
        description: 'Consider how validation planning should reflect the team that has to sustain the system.',
        to: '/product/validation-planning-for-sustainable-teams',
      },
      {
        label: 'Reducing quality-system administration',
        description: 'Separate necessary controls from repetitive coordination and administrative work.',
        to: '/product/reducing-quality-system-administration',
      },
      {
        label: 'Practical GxP Quality Systems',
        description: 'Read the overview of QikSolve quality-system pathways and related practice areas.',
        to: '/product/quality-systems',
      },
    ],
  },
  cta: {
    title: 'Decide what your team can sustain',
    content:
      'Before moving to the next pathway, document the first workflow, accountable owners, review capacity, and the sequence the team can sustain.',
    label: 'Discuss your implementation sequence',
    to: '/contact',
  },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
